import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Check, Loader2, Shield, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReshuffleConsentPanel({ mode = "instructor" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/diary-intelligence/reshuffles/pending`,
        { withCredentials: true }
      );
      setItems(data.items || []);
    } catch { setItems([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action, body) => {
    setBusy(`${id}:${action}`);
    try {
      await axios.post(
        `${API}/api/diary-intelligence/reshuffles/${id}/${action}`,
        body || {}, { withCredentials: true }
      );
      toast.success(action === "approve" ? "Consent requests sent" :
        action === "commit" ? "Diary updated" : "Response recorded");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || "This change could not be completed");
    }
    setBusy("");
  };

  if (loading) return <div className="py-3 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-blue-500" /></div>;
  if (!items.length) return null;

  return (
    <section data-testid={`reshuffle-panel-${mode}`} className="space-y-3 mb-4">
      <div className="flex items-center gap-2 px-1">
        <CalendarClock className="w-4 h-4 text-blue-600" />
        <h2 className="text-sm font-extrabold text-slate-900 dark:text-white">
          {mode === "learner" ? "Lesson change requests" : "Diary changes awaiting decisions"}
        </h2>
      </div>
      {items.map(item => (
        <div key={item.id} className="bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 rounded-2xl p-4">
          <div className="flex items-start gap-2 mb-3">
            <Shield className="w-4 h-4 text-emerald-600 mt-0.5" />
            <p className="text-xs text-slate-600 dark:text-slate-300">
              {mode === "learner"
                ? "Your current lesson remains booked unless you accept this change."
                : `State: ${item.state.replaceAll("_", " ").toLowerCase()}`}
            </p>
          </div>
          <div className="space-y-2">
            {item.working_hours_extension?.required && (
              <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
                <p className="text-xs font-extrabold text-amber-800 dark:text-amber-300">One-off working-hours extension</p>
                <p className="text-sm text-slate-700 dark:text-slate-200 mt-1">
                  Normal finish {item.working_hours_extension.normal_finish} · proposed finish {item.working_hours_extension.proposed_finish}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                  {item.working_hours_extension.extension_minutes} extra minutes. This will not change your normal hours.
                </p>
              </div>
            )}
            {(item.affected_lessons || []).map(move => (
              <div key={move.lesson_id} className="rounded-xl bg-slate-50 dark:bg-slate-700/50 p-3 text-sm">
                <p className="text-slate-500 line-through">{move.original.date} · {move.original.start_time}</p>
                <p className="font-extrabold text-slate-900 dark:text-white">{move.proposed.date} · {move.proposed.start_time}</p>
                {mode === "instructor" && <p className="text-[10px] mt-1 text-slate-400">Pupil: {move.consent.toLowerCase()}</p>}
              </div>
            ))}
          </div>
          {mode === "learner" && (
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button onClick={() => act(item.id, "respond", { accepted: false })}
                disabled={!!busy} className="h-11 rounded-xl border border-slate-300 font-bold text-slate-600 flex items-center justify-center gap-2">
                <X className="w-4 h-4" /> Keep original
              </button>
              <button onClick={() => act(item.id, "respond", { accepted: true })}
                disabled={!!busy} className="h-11 rounded-xl bg-blue-600 text-white font-bold flex items-center justify-center gap-2">
                <Check className="w-4 h-4" /> Accept change
              </button>
            </div>
          )}
          {mode === "instructor" && item.state === "AWAITING_INSTRUCTOR_APPROVAL" && (
            <button onClick={() => act(item.id, "approve", {
                allow_working_hours_extension: !!item.working_hours_extension?.required
              })} disabled={!!busy}
              className="w-full min-h-11 mt-3 px-3 py-2 rounded-xl bg-blue-600 text-white font-bold">
              {item.working_hours_extension?.required
                ? "Approve this one-off extension"
                : item.affected_lessons?.length ? "Ask affected pupils" : "Approve proposal"}
            </button>
          )}
          {mode === "instructor" && item.state === "READY_TO_COMMIT" && (
            <button onClick={() => act(item.id, "commit") } disabled={!!busy}
              className="w-full h-11 mt-3 rounded-xl bg-emerald-600 text-white font-bold">Confirm agreed diary change</button>
          )}
          {item.state === "DECLINED" && mode === "instructor" && (
            <p className="mt-3 text-xs font-bold text-amber-700">Declined — every original booking remains protected.</p>
          )}
        </div>
      ))}
    </section>
  );
}
